import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Minus } from "lucide-react";

// ─── Period Selector ──────────────────────────────────────────────────────────

export type TrendPeriod = "current" | "3m" | "6m" | "ytd";

const PERIOD_LABELS: Record<TrendPeriod, string> = {
  current: "This Month",
  "3m": "3M",
  "6m": "6M",
  ytd: "YTD",
};

export function PeriodSelector({
  value,
  onChange,
}: {
  value: TrendPeriod;
  onChange: (period: TrendPeriod) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 bg-muted/60 rounded-lg p-1" data-testid="period-selector">
      {(["current", "3m", "6m", "ytd"] as TrendPeriod[]).map((p) => (
        <Button
          key={p}
          size="sm"
          variant={value === p ? "default" : "ghost"}
          className="h-6 text-xs px-2 py-0"
          onClick={() => onChange(p)}
          data-testid={`period-btn-${p}`}
        >
          {PERIOD_LABELS[p]}
        </Button>
      ))}
    </div>
  );
}

// ─── On-Track Badge ───────────────────────────────────────────────────────────

export function OnTrackBadge({ isOnTrack }: { isOnTrack: boolean | null }) {
  const { t } = useTranslation(["pages", "common"]);
  if (isOnTrack === null) return null;
  if (isOnTrack) {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 gap-1 text-xs" data-testid="badge-on-track">
        <CheckCircle2 className="w-3 h-3" />{t("pages:trendCharts.onTrack")}
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800 gap-1 text-xs" data-testid="badge-off-track">
      <XCircle className="w-3 h-3" />{t("pages:trendCharts.offTrack")}
    </Badge>
  );
}

// ─── TrendLineChart ──────────────────────────────────────────────────────────
// Recharts-based line chart supporting actual/projected/goal series.
// For "current" period: shows daily data points.
// For multi-period: shows monthly data points.

interface TrendLineChartProps {
  chartData: Array<{
    day?: number;
    label?: string;
    actual: number | null;
    projected: number | null;
    goal: number | null;
  }>;
  historicalChartData?: Array<{
    period: string;
    periodName: string;
    isCurrentMonth: boolean;
    actual: number | null;
    projected: number | null;
    goal: number | null;
    onTrack: boolean | null;
  }>;
  period: TrendPeriod;
  metric: "dealer_count" | "mrr";
  isOnTrack: boolean | null;
  height?: number;
}

function fmtValue(value: number, metric: "dealer_count" | "mrr") {
  if (metric === "mrr") return `$${value.toLocaleString()}`;
  return String(value);
}

export function TrendLineChart({
  chartData,
  historicalChartData,
  period,
  metric,
  isOnTrack,
  height = 200,
}: TrendLineChartProps) {
  const isCurrentPeriod = period === "current";

  if (isCurrentPeriod) {
    const hasGoal = chartData.some((d) => d.goal !== null);
    const onTrackColor = isOnTrack === null
      ? "hsl(var(--primary))"
      : isOnTrack
      ? "#16a34a"
      : "#dc2626";

    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="day"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval={4}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => metric === "mrr" ? `$${(v / 1000).toFixed(0)}k` : String(v)}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value: any, name: string) => {
              const labels: Record<string, string> = { actual: "Actual", projected: "Projected", goal: "Goal" };
              return [fmtValue(value, metric), labels[name] ?? name];
            }}
            labelFormatter={(label) => `Day ${label}`}
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke={onTrackColor}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            name="actual"
          />
          <Line
            type="monotone"
            dataKey="projected"
            stroke={onTrackColor}
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            connectNulls={false}
            name="projected"
          />
          {hasGoal && (
            <Line
              type="monotone"
              dataKey="goal"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1}
              strokeDasharray="2 4"
              dot={false}
              name="goal"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Multi-period historical view
  const multiData = (historicalChartData ?? []).map((d) => ({
    name: d.periodName,
    actual: d.actual,
    projected: d.projected,
    goal: d.goal,
    onTrack: d.onTrack,
    isCurrentMonth: d.isCurrentMonth,
  }));

  const hasGoal = multiData.some((d) => d.goal !== null);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={multiData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10 }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 10 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => metric === "mrr" ? `$${(v / 1000).toFixed(0)}k` : String(v)}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8 }}
          formatter={(value: any, name: string) => {
            const labels: Record<string, string> = { actual: "Actual", projected: "Projected (EOD)", goal: "Goal" };
            return [fmtValue(value, metric), labels[name] ?? name];
          }}
        />
        <Line
          type="monotone"
          dataKey="actual"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          dot={(props: any) => {
            const d = multiData[props.index];
            if (!d) return <></>;
            const color = d.onTrack === true ? "#16a34a" : d.onTrack === false ? "#dc2626" : "hsl(var(--primary))";
            return <circle key={props.index} cx={props.cx} cy={props.cy} r={4} fill={color} stroke="white" strokeWidth={1.5} />;
          }}
          connectNulls={false}
          name="actual"
        />
        {multiData.some((d) => d.projected !== null) && (
          <Line
            type="monotone"
            dataKey="projected"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            connectNulls={false}
            name="projected"
          />
        )}
        {hasGoal && (
          <Line
            type="monotone"
            dataKey="goal"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth={1}
            strokeDasharray="2 4"
            dot={false}
            name="goal"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── SparklineChip ────────────────────────────────────────────────────────────
// Compact inline sparkline for cards and table rows.
// Takes an array of numeric values and renders a small line chart.

interface SparklineChipProps {
  data: number[];
  isOnTrack: boolean | null;
  width?: number;
  height?: number;
  metric?: "dealer_count" | "mrr";
  labels?: string[];
}

export function SparklineChip({
  data,
  isOnTrack,
  width = 80,
  height = 32,
  metric = "dealer_count",
  labels,
}: SparklineChipProps) {
  if (data.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground"
        style={{ width, height }}
        data-testid="sparkline-empty"
      >
        <Minus className="w-3 h-3" />
      </div>
    );
  }

  const chartData = data.map((v, i) => ({ i, v }));
  const color = isOnTrack === null
    ? "hsl(var(--muted-foreground))"
    : isOnTrack
    ? "#16a34a"
    : "#dc2626";

  return (
    <div style={{ width, height }} data-testid="sparkline-chip">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
          <Tooltip
            contentStyle={{
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 6,
              backgroundColor: "hsl(var(--popover))",
              borderColor: "hsl(var(--border))",
              color: "hsl(var(--popover-foreground))",
            }}
            cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeDasharray: "3 3" }}
            formatter={(value: any) => [fmtValue(value, metric), ""]}
            labelFormatter={(idx: number) => labels?.[idx] ?? ""}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
